import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Claim, User } from '@clan-heart/shared';
import { getClaims, reviewClaim } from '../api.js';
import { Button } from '../components/Button.js';
import styles from './ReviewPage.module.css';

function ReviewRow({ claim, me }: { claim: Claim; me: User }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      reviewClaim(claim.id, { decision, note: note.trim() === '' ? undefined : note.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  // The server also enforces this (403) — hiding the buttons here is just so
  // an officer never sees a control that would fail. The owner reviewing
  // their own claim is allowed, by id, never by display name.
  const canReview = !(me.role === 'officer' && me.id === claim.member.id);

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <span className={styles.title}>
          {claim.accomplishment.title} · {claim.accomplishment.points} pts
        </span>
        <span className={styles.meta}>
          {claim.member.displayName} · submitted {new Date(claim.submittedAt).toLocaleDateString()}
        </span>
      </div>
      {claim.note && <p className={styles.note}>{claim.note}</p>}
      {claim.evidenceUrl && (
        <p className={styles.evidence}>
          <a href={claim.evidenceUrl} target="_blank" rel="noopener noreferrer">
            Evidence link
          </a>
        </p>
      )}
      {canReview && (
        <>
          <div className={styles.reviewNoteField}>
            <label htmlFor={`review-note-${claim.id}`}>Reviewer note (optional)</label>
            <input
              id={`review-note-${claim.id}`}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className={styles.actions}>
            <Button
              type="button"
              variant="primary"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('approve')}
            >
              Approve
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('reject')}
            >
              Reject
            </Button>
          </div>
          {error && <p className={styles.error}>{error}</p>}
        </>
      )}
    </article>
  );
}

export function ReviewPage({ me }: { me: User }) {
  const query = useQuery({
    queryKey: ['claims', { status: 'pending' }],
    queryFn: () => getClaims({ status: 'pending' }),
  });

  if (query.isLoading) return <p>Loading…</p>;
  if (query.isError) return <p>{(query.error as Error).message}</p>;

  const claims = query.data?.claims ?? [];

  return (
    <section>
      <h2 className={styles.heading}>Review queue</h2>
      {claims.length === 0 && <p className={styles.empty}>Nothing waiting for review.</p>}
      <div className={styles.list}>
        {claims.map((claim) => (
          <ReviewRow key={claim.id} claim={claim} me={me} />
        ))}
      </div>
    </section>
  );
}
